import { BookingFilterValue } from './../../../shared/interfaces'
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker'
import { RequestQueryBuilder, CondOperator } from '@nestjsx/crud-request'
import { bookingActions } from './../../booking/booking.actions'
import { bookingSelectors } from './../../booking/booking.selectors'
import { ActivatedRoute, Router } from '@angular/router'
import { Booking } from './../../booking/booking'
import { combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  timeout
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'

import { Component, OnInit } from '@angular/core'
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { Store } from '@ngrx/store'

import { Actions, ofType } from '@ngrx/effects'
import { NotificationService } from 'src/app/core/notification.service'
import moment from 'moment-mini'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { participationSelectors } from '../../booking/participation/participation.selectors'
import { Organization } from '../../organization/organization'
import { organizationSelectors } from '../../organization/organization.selectors'
import { organizationActions } from '../../organization/organization.actions'
import { OrganizationService } from '../organization.service'

@UntilDestroy()
@Component({
  selector: 'app-booking-tab',
  templateUrl: './booking-tab.component.html',
  styleUrls: ['./booking-tab.component.scss']
})
export class OrganizationBookingTabComponent implements OnInit {
  public organization$: Observable<Organization>
  public organizationId$: Observable<number>
  public organizationBookings$: Observable<Booking[]>

  public form: UntypedFormGroup
  public navigationForm: UntypedFormGroup
  public isLoading$ = this.store.select(bookingSelectors.selectIsLoading)
  public moduleConfigs: any
  private bookingsFetched: boolean = false

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private fb: UntypedFormBuilder,
    private notificationService: NotificationService,
    private router: Router,
    private organizationService: OrganizationService,
    private activeRoute: ActivatedRoute
  ) {}

  ngOnInit() {
    this.navigationForm = this.fb.group({
      selectedType: ['bookings', []]
    })
    this.isLoading$ = this.store.select(bookingSelectors.selectIsLoading)
    if (this.organizationService.showModal) {
      this.activeRoute.params.pipe(take(1)).subscribe((params) => {
        if (params.hasOwnProperty('organizationId')) {
          this.organizationId$ = of(Number(params['organizationId']))
        }
      })
    } else {
      this.organizationId$ = this.store
        .select(organizationSelectors.selectIdRouteParam)
        .pipe(filter((id) => Boolean(id)))
    }

    this.organization$ = this.organizationId$.pipe(
      switchMap((organizationId) =>
        this.store.select(organizationSelectors.selectById, organizationId)
      )
    )

    this.form = this.fb.group({
      dateRange: [
        [
          moment().startOf('day').toDate(),
          moment().startOf('day').add(30, 'days').toDate()
        ],
        []
      ]
    })

    this.organizationBookings$ = combineLatest([
      this.store.select(bookingSelectors.selectAll),
      this.store.select(participationSelectors.selectEntities),
      this.organization$
    ]).pipe(
      map(([bookings, participations, organization]) => {
        if (!organization) {
          return []
        }
        const [startDate, endDate] = this.form.controls.dateRange.value || []
        if (!startDate || !endDate) {
          return []
        }

        const startOfRange = moment(startDate).startOf('day').valueOf()
        const endOfRange = moment(endDate).endOf('day').valueOf()

        // enrich booking for searching in table
        return bookings
          .map((booking) => ({
            ...booking
          }))
          .filter(
            (b) =>
              moment(b.startDate).valueOf() <= endOfRange &&
              moment(b.startDate).valueOf() >= startOfRange &&
              b.participations?.length > 0 &&
              this.organizationIsInParticipation(
                participations,
                b,
                organization.id
              )
          )
      }),
      map((bookings) =>
        bookings.sort((a, b) =>
          moment(a.startDate).valueOf() < moment(b.startDate).valueOf() ? -1 : 1
        )
      )
    )

    combineLatest([this.form.controls.dateRange.valueChanges])
      .pipe(untilDestroyed(this))
      .subscribe(([dateRange]) => {
        if (!dateRange?.[0] || !dateRange?.[1]) {
          return
        }

        this.getMemberBookings({
          startDate: moment(dateRange[0]).startOf('day'),
          endDate: moment(dateRange[1]).endOf('day')
        })
      })
    this.getMemberBookings({
      startDate: moment().startOf('day'),
      endDate: moment().add(30, 'day').endOf('day')
    })
  }
  private organizationIsInParticipation(
    participations,
    booking,
    organizationId
  ) {
    return (
      booking.participations.filter(
        (p) => participations[p].organizationId === organizationId
      )?.length > 0
    )
  }
  public onClickRow(booking: Booking) {
    this.router.navigateByUrl(`/bookings/${booking.id}`)
  }

  public onSubmit(form): void {
    this.organizationId$.pipe(take(1)).subscribe((organizationId) =>
      this.store.dispatch(
        organizationActions.updateOneById({
          id: organizationId,
          params: {
            ...form,
            id: parseInt(organizationId.toString())
          }
        })
      )
    )

    this.actions$
      .pipe(
        ofType(organizationActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public getMemberBookings({
    startDate = moment().startOf('day'),
    endDate = moment().add(30, 'day').endOf('day')
  }: Partial<BookingFilterValue>) {
    if (!this.organizationId$) {
      return
    }

    const startDateIso = moment(startDate).startOf('day').toISOString()
    const endDateIso = moment(endDate).endOf('day').toISOString()

    this.organizationId$.pipe(take(1)).subscribe((organizationId) => {
      this.store.dispatch(
        bookingActions.fetchMany({
          params: RequestQueryBuilder.create({
            search: {
              startDate: {
                [CondOperator.BETWEEN]: [startDateIso, endDateIso]
              },
              'participations.organizationId': organizationId
            },
            join: [
              { field: 'product' },
              { field: 'participations' },
              { field: 'linkedProduct' }
            ]
          })
        })
      )
    })
  }
  public onClickSelectedType(type: string) {
    this.navigationForm.controls.selectedType.patchValue(type)
  }
}
